import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { styled } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { PlayerGameScoreScore } from '@sorare/core/src/components/scoring/PlayerGameScoreScore';
import {
  isSubstitutedIn,
  isSubstitutedOut,
} from '@sorare/marketplace/src/lib/playingStatus';
import { SubIcon } from '@sorare/marketplace/src/shared/stats/SubIcon';

import type { AppearanceAvatarWithScore_cardWithPlayerGameScoreInterface } from './__generated__/index.graphql.ts';

const PlayerAvatar = styled.div`
  position: relative;
  z-index: 1;
  isolation: isolate;
  display: flex;
  justify-content: center;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--c-nd-150);

  img {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    object-fit: cover;
    object-position: center;
    transition: opacity 0.2s ease-in-out;
  }
`;
const PlayerIcon = styled.div`
  position: absolute;
  top: 0;
  left: calc(-1 * var(--half-unit));
  font-size: 0;
  z-index: 5;
`;

const ScoreOverlay = styled.div`
  position: relative;
  transform: translateY(-50%);

  z-index: 10;

  &:has(*) {
    margin-bottom: calc(-1 * var(--double-unit));
  }
`;

const PlayerButton = styled(LinkOther)`
  position: relative;
  isolation: isolate;

  &:hover:not(:disabled) img {
    opacity: 0.5;
  }
  margin-bottom: calc(-1 * var(--half-unit));
`;

type Props = {
  cardWithPlayerGameScoreInterface: AppearanceAvatarWithScore_cardWithPlayerGameScoreInterface;
  onClick?: () => void;
};
export const AppearanceAvatarWithScore = ({
  cardWithPlayerGameScoreInterface,
  onClick,
}: Props) => {
  const { card, playerGameScore } = cardWithPlayerGameScoreInterface;
  const substitutedIn = isSubstitutedIn(playerGameScore);
  const substitutedOut = isSubstitutedOut(playerGameScore);

  const isUsedInLineup =
    playerGameScore?.anyPlayerGameStats.myBestSo5Appearance;

  return (
    <PlayerButton
      as={ButtonBase}
      disabled={!onClick}
      className={classNames({ clickable: !!onClick })}
      onClick={onClick}
    >
      <div>
        {!!(substitutedIn || substitutedOut) && (
          <PlayerIcon>
            <SubIcon type={substitutedIn ? 'sub_in' : 'sub_out'} />
          </PlayerIcon>
        )}
        <Vertical gap={0} center>
          <PlayerAvatar
            style={{ filter: isUsedInLineup ? 'none' : 'grayscale(1)' }}
          >
            <ResponsiveImg
              src={card?.avatarUrl}
              cropWidth={80}
              alt=""
              width="24"
            />
          </PlayerAvatar>
          {!!playerGameScore && (
            <ScoreOverlay>
              <PlayerGameScoreScore
                playerGameScore={playerGameScore}
                shape="PILL"
                size={3}
                hidePending
              />
            </ScoreOverlay>
          )}
        </Vertical>
      </div>
    </PlayerButton>
  );
};

AppearanceAvatarWithScore.fragments = {
  cardWithPlayerGameScoreInterface: gql`
    fragment AppearanceAvatarWithScore_cardWithPlayerGameScoreInterface on AnyCardWithPlayerGameScore {
      id
      card {
        slug
        avatarUrl: pictureUrl(derivative: "avatar")
      }
      playerGameScore {
        id
        score
        anyGame {
          id
          status: statusTyped
        }
        anyPlayerGameStats {
          id
          myBestSo5Appearance {
            id
          }
        }
        ...PlayerGameScoreScore_playerGameScoreInterface
        ...isSubstitutedIn_playerGameScore
        ...isSubstitutedOut_playerGameScore
      }
    }
    ${PlayerGameScoreScore.fragments.playerGameScoreInterface}
    ${isSubstitutedIn.fragments.playerGameScore}
    ${isSubstitutedOut.fragments.playerGameScore}
  ` as TypedDocumentNode<AppearanceAvatarWithScore_cardWithPlayerGameScoreInterface>,
};
